import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { IconDoubleChecked, IconRightArrow } from "icons";
import { useAppDispatch, useAppSelector } from "store";
import { notificationsService } from "api/notifications";
import {
  selectNotifications,
  selectNotificationsHasMore,
  selectNotificationsPage,
  selectNotificationUnreadCount,
  appendNotifications,
  markItemsSeen,
  markItemsRead,
  setUnreadCount,
} from "store/notificationsSlice";
import { useSentinelObserver } from "hooks/useSentinelObserver";
import { PATHS } from "router/config/paths";
import NotificationItem from "./NotificationItem";

// Mirrors the boot fetch's page size (useNotificationsBoot.ts) so a full page always fills the panel.
const PAGE_SIZE = 20;

interface NotificationsListProps {
  // Only true while the panel is open AND this tab is the active one — gates both the
  // mark-seen-on-open effect and the infinite-scroll sentinel (Trap #15: node-keyed, never a
  // hand-rolled IntersectionObserver).
  isActive: boolean;
  // Closes the panel before navigating to the full Notifications page (see-all footer).
  onClose: () => void;
}

const NotificationsList = ({ isActive, onClose }: NotificationsListProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const items = useAppSelector(selectNotifications);
  const page = useAppSelector(selectNotificationsPage);
  const hasMore = useAppSelector(selectNotificationsHasMore);
  const unreadCount = useAppSelector(selectNotificationUnreadCount);
  const isLoadingMoreRef = useRef(false);
  const markedSeenRef = useRef(false);

  // Mark the currently-loaded unseen items seen once per tab activation (not on every items change).
  useEffect(() => {
    if (!isActive) {
      markedSeenRef.current = false;

      return;
    }

    if (markedSeenRef.current) return;
    markedSeenRef.current = true;

    const unseenIds = items.filter((item) => item.seen_at == null).map((item) => item.id);
    if (unseenIds.length === 0) return;

    dispatch(markItemsSeen(unseenIds));
    notificationsService.markSeen(unseenIds).catch(() => undefined);
    // Intentionally keyed on `isActive` only — `markedSeenRef` is the re-run guard, and `items`/`dispatch`
    // reads inside always see the latest closure value on the one run that matters.
  }, [isActive]);

  const loadMore = async () => {
    if (isLoadingMoreRef.current || !hasMore) return;
    isLoadingMoreRef.current = true;

    try {
      const nextPage = page + 1;
      const res = await notificationsService.getFeed({ page: nextPage, page_size: PAGE_SIZE });

      if (Array.isArray(res?.items)) {
        dispatch(appendNotifications({ items: res.items, page: res.pagination?.page ?? nextPage, hasMore: res.pagination?.has_more ?? false }));
      }
    } catch {
      /* leave hasMore as-is — the sentinel simply retries on next intersection */
    } finally {
      isLoadingMoreRef.current = false;
    }
  };

  const setSentinel = useSentinelObserver(loadMore, { enabled: isActive && hasMore });

  const handleMarkAllRead = () => {
    const unreadIds = items.filter((item) => item.read_at == null).map((item) => item.id);
    if (unreadIds.length > 0) dispatch(markItemsRead(unreadIds));
    dispatch(setUnreadCount(0));
    notificationsService.markRead().catch(() => undefined);
  };

  const handleSeeAll = () => {
    onClose();
    navigate(PATHS.NOTIFICATIONS);
  };

  return (
    <div className="flex flex-col">
      <div className="flex max-h-[60vh] flex-col gap-1 overflow-y-auto p-2">
        {items.length === 0 ? (
          <div className="px-4 py-10 text-center text-[13px] text-grey">{t("common:whats_new.notifications.empty")}</div>
        ) : (
          <>
            {items.map((item) => (
              <NotificationItem key={item.id} item={item} />
            ))}
            {hasMore && <div ref={setSentinel} className="h-1 w-full" />}
          </>
        )}
      </div>

      {items.length > 0 && (
        <div className="flex items-center gap-2 border-t border-black-300 p-2">
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllRead}
              className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-[13px] font-medium text-trueBlack dark:text-white hover:bg-black-200/60 transition-colors"
            >
              <IconDoubleChecked className="h-4 w-4" />
              {t("notifications:page.mark_all_read")}
            </button>
          )}
          <button
            onClick={handleSeeAll}
            className="ml-auto flex items-center gap-1.5 rounded-lg px-3 py-2 text-[13px] font-medium text-trueBlack dark:text-white hover:bg-black-200/60 transition-colors"
          >
            {t("common:whats_new.see_all")}
            <IconRightArrow className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationsList;
